import { AttachFile, ChatOutlined, ErrorOutline, Refresh, Search } from "@mui/icons-material";
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Pagination,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { fetchClientWhatsAppLogs, fetchWhatsAppMessageFileUrl } from "api/whatsapp";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { getApiError } from "utils/apiError";
import { formatDisplayDateTime } from "utils/dateFormat";

const statusColor = (status) => (status === "SUCCESS" || status === "SENT" ? "success" : "error");

const ClientWhatsApp = ({ clientId }) => {
  const [filters, setFilters] = useState({ page: 0, size: 10, search: "" });
  const [selectedId, setSelectedId] = useState(null);
  const [opening, setOpening] = useState(false);

  useEffect(() => {
    setFilters((prev) => ({ ...prev, page: 0 }));
    setSelectedId(null);
  }, [clientId]);

  const { data, isFetching, refetch } = useQuery(
    ["client-whatsapp-logs", clientId, filters],
    () => fetchClientWhatsAppLogs(clientId, filters),
    { enabled: !!clientId, keepPreviousData: true, refetchOnWindowFocus: false }
  );

  const messages = data?.content || [];
  const selected = useMemo(
    () => messages.find((row) => row.id === selectedId) || messages[0] || null,
    [messages, selectedId]
  );

  useEffect(() => {
    if (messages.length > 0 && !messages.some((row) => row.id === selectedId)) {
      setSelectedId(messages[0].id);
    }
  }, [messages, selectedId]);

  const updateSearch = (value) => setFilters((prev) => ({ ...prev, search: value, page: 0 }));

  const openFile = async (message) => {
    if (!message?.id || opening) return;
    setOpening(true);
    try {
      const url = await fetchWhatsAppMessageFileUrl(message.id);
      if (!url) throw new Error("empty link");
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      toast.error(getApiError(err, "Could not open this file. Please try again."));
    } finally {
      setOpening(false);
    }
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
      <Paper
        elevation={0}
        className="xl:col-span-5 border border-app-border rounded-lg overflow-hidden bg-app-surface"
      >
        <div className="p-4 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 border-b border-app-border">
          <TextField
            size="small"
            label="Search WhatsApp"
            value={filters.search}
            onChange={(event) => updateSearch(event.target.value)}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Tooltip title="Refresh">
            <IconButton onClick={() => refetch()} disabled={isFetching}>
              <Refresh />
            </IconButton>
          </Tooltip>
        </div>
        <div className="divide-y divide-slate-100 min-h-[380px] max-h-[560px] overflow-y-auto">
          {messages.length === 0 && (
            <div className="h-[380px] flex flex-col items-center justify-center text-center px-6">
              <ChatOutlined className="text-slate-300 mb-2" sx={{ fontSize: 48 }} />
              <Typography className="font-semibold text-app-text">
                No WhatsApp messages tracked
              </Typography>
              <Typography variant="body2" className="text-app-text-muted">
                WhatsApp messages sent to this client will appear here.
              </Typography>
            </div>
          )}
          {messages.map((row) => {
            const active = selected?.id === row.id;
            return (
              <button
                key={row.id}
                type="button"
                onClick={() => setSelectedId(row.id)}
                className={`w-full text-left px-4 py-3 ${
                  active ? "bg-app-surface-hover" : "bg-app-surface hover:bg-app-surface-muted"
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <Typography className="font-semibold text-app-text truncate">
                      {row.subject || row.body || "(No text)"}
                    </Typography>
                    <Typography variant="body2" className="text-app-text-muted truncate">
                      {row.phone || "—"}
                      {row.mediaName ? ` · ${row.mediaName}` : ""}
                    </Typography>
                  </div>
                  <Chip
                    size="small"
                    label={row.status || "UNKNOWN"}
                    color={statusColor(row.status)}
                    variant={row.status === "FAILED" ? "filled" : "outlined"}
                  />
                </div>
                <Typography variant="caption" className="text-app-text-muted mt-2 block">
                  {formatDisplayDateTime(row.sentAt)}
                </Typography>
              </button>
            );
          })}
        </div>
        <Divider />
        <Box className="px-4 py-3 flex items-center justify-between gap-2">
          <Typography variant="body2" className="text-app-text-muted">
            {data?.totalItems || 0} messages
          </Typography>
          <Pagination
            size="small"
            count={Math.max(data?.totalPages || 1, 1)}
            page={(data?.currentPage || 0) + 1}
            onChange={(_, page) => setFilters((prev) => ({ ...prev, page: page - 1 }))}
            color="primary"
          />
        </Box>
      </Paper>

      <Paper
        elevation={0}
        className="xl:col-span-7 border border-app-border rounded-lg overflow-hidden bg-app-surface"
      >
        {!selected ? (
          <div className="min-h-[480px] flex flex-col items-center justify-center text-center px-6">
            <ChatOutlined className="text-slate-300 mb-2" sx={{ fontSize: 48 }} />
            <Typography className="font-semibold text-app-text">Select a message</Typography>
          </div>
        ) : (
          <>
            <div className="px-5 py-4 border-b border-app-border">
              <Typography variant="h6" className="font-semibold text-app-text break-words">
                {selected.subject || "(No subject)"}
              </Typography>
              <Typography variant="body2" className="text-app-text-muted mt-1">
                {formatDisplayDateTime(selected.sentAt)}
              </Typography>
            </div>
            <div className="px-5 py-4 grid grid-cols-1 md:grid-cols-2 gap-3 bg-app-surface-muted/70 border-b border-app-border">
              <div>
                <Typography variant="caption" className="text-app-text-muted font-bold uppercase">
                  To
                </Typography>
                <Typography variant="body2" className="break-words">
                  {selected.phone || "—"}
                </Typography>
              </div>
              <div>
                <Typography variant="caption" className="text-app-text-muted font-bold uppercase">
                  Sent by
                </Typography>
                <Typography variant="body2" className="break-words">
                  {selected.sentByName || selected.sentByEmail || "—"}
                </Typography>
              </div>
              <div>
                <Typography variant="caption" className="text-app-text-muted font-bold uppercase">
                  Source
                </Typography>
                <Typography variant="body2">{selected.source || "—"}</Typography>
              </div>
              <div>
                <Typography variant="caption" className="text-app-text-muted font-bold uppercase">
                  Status
                </Typography>
                <Chip
                  size="small"
                  label={selected.status || "UNKNOWN"}
                  color={statusColor(selected.status)}
                  variant={selected.status === "FAILED" ? "filled" : "outlined"}
                />
              </div>
              {selected.errorMessage && (
                <div className="md:col-span-2 flex gap-2 p-3 rounded-md bg-red-50 text-red-700 border border-red-100">
                  <ErrorOutline fontSize="small" />
                  <Typography variant="body2">{selected.errorMessage}</Typography>
                </div>
              )}
              {selected.hasFile && selected.mediaName && (
                <div className="md:col-span-2">
                  <Chip
                    size="small"
                    icon={opening ? <CircularProgress size={12} /> : <AttachFile />}
                    label={opening ? `Opening ${selected.mediaName}…` : selected.mediaName}
                    variant="outlined"
                    onClick={() => openFile(selected)}
                    sx={{ cursor: "pointer" }}
                  />
                </div>
              )}
            </div>
            <div className="p-5 overflow-y-auto max-h-[520px] min-h-[260px] whitespace-pre-wrap text-sm text-app-text">
              {selected.body || "No message captured."}
            </div>
          </>
        )}
      </Paper>
    </div>
  );
};

export default ClientWhatsApp;
